// Audience Requirements on the learner's lesson page (DA-5056, PRD rules 5 and 6):
// a lesson the learner only reads (`access: 'readOnly'` on GET elliott-lesson)
// is its Lesson Body, case studies and video. Its Roleplay and Quiz are not for
// them, so the page takes the lesson as if it had neither: no stepper node, no
// step to stop on, nothing to answer before Mark Complete. Read only while
// audienceRequirementsBackend() is on (ProgramSettings/requirements/flags.ts);
// with it off the page takes every lesson as it is served.
//
// The backend refuses a Roleplay or Quiz attempt by someone in a Read Only group
// (409 { message, readOnly: true }); the page then drops them the same way.
// No React here, so it can be tested on its own.

import type { LessonAccess } from '../../../../../api/audienceRequirements';

/** The parts of the served lesson its assessments live in. */
export interface AssessedLesson {
  access?: LessonAccess;
  simulation: unknown | null;
  quiz: unknown[];
  quizChecks?: unknown[];
  partBadges?: Partial<Record<string, 'new' | 'updated'>>;
  nav: { modules: Array<{ lessons: AccessNavLesson[] }> };
}

/** A lesson in the nav, with what this learner must do on it. */
export interface AccessNavLesson {
  id: string;
  access?: LessonAccess;
  /** An assessment left to come back for (Improve Performance). */
  flagged?: boolean;
  /** What a publish asked them to redo here. */
  pendingAction?: { kind: string; optional: boolean } | null;
}

/** What a publish may ask a learner to redo that a lesson they only read never asks: its assessments. */
const ASSESSED_ACTIONS = new Set(['quiz', 'scenario']);

/** The lesson without its Roleplay and Quiz: what a learner who only reads it takes. */
export const withoutAssessments = <T extends AssessedLesson>(lesson: T): T => {
  // The New / Updated marks of the parts that go, go with them.
  const { liveScenario, quiz, ...badges } = lesson.partBadges ?? {};
  const read: T = {
    ...lesson,
    simulation: null,
    quiz: [],
    quizChecks: [],
    ...(lesson.partBadges ? { partBadges: badges } : {})
  };
  return read;
};

/**
 * The nav as this learner reads it: a lesson they only read has no assessment,
 * so nothing to come back for (no red flag) and no Roleplay or Quiz to redo.
 */
export const navForAccess = <M extends { lessons: AccessNavLesson[] }>(modules: M[]): M[] =>
  modules.map((m) => ({
    ...m,
    lessons: m.lessons.map((l) =>
      l.access === 'readOnly'
        ? {
            ...l,
            flagged: false,
            pendingAction: l.pendingAction && ASSESSED_ACTIONS.has(l.pendingAction.kind) ? null : l.pendingAction
          }
        : l
    )
  }));

/**
 * The lesson as this learner takes it. With the backend on, a lesson they only
 * read drops its Roleplay and Quiz, and the nav its assessments; with it off,
 * every lesson is as it is served.
 */
export const lessonForLearner = <T extends AssessedLesson>(lesson: T, backend: boolean): T => {
  if (!backend) return lesson;
  const read = lesson.access === 'readOnly' ? withoutAssessments(lesson) : lesson;
  const taken: T = { ...read, nav: { ...read.nav, modules: navForAccess(read.nav?.modules ?? []) } };
  return taken;
};

/** A Roleplay or Quiz attempt refused because the learner only reads this course (409 { readOnly: true }). */
export const readOnlyRefusal = (err: unknown): { message: string } | null => {
  const res = (err as { response?: { status?: number; data?: { readOnly?: unknown; message?: unknown } } } | null)
    ?.response;
  if (res?.status !== 409 || res.data?.readOnly !== true) return null;
  return { message: typeof res.data.message === 'string' ? res.data.message : '' };
};
